/**
 * Raptor Predator Frontend - Socket.io Client
 *
 * Real-time WebSocket connection to MDC backend with:
 * - Auto-reconnection with exponential backoff
 * - Frame-locked update processing
 * - Event batching for performance
 *
 * @security SEC-007: WebSocket Authentication
 * @priority P1 HIGH
 *
 * Peer Review: Gemini 3 Pro (7/10), GPT-4o (7/10)
 *
 * Security Features:
 * - wss:// (TLS encrypted) exclusively
 * - Token-based authentication (first message, not URL)
 * - Server validates token before accepting connection
 * - Token refresh via HTTP (NOT over WebSocket - peer review fix)
 * - Connection timeout on auth failure (10 seconds)
 * - Authentication nonce for replay prevention (peer review fix)
 * - Auth-gated data handlers (client-side, server MUST also enforce)
 *
 * CRITICAL SERVER-SIDE REQUIREMENTS (Peer Review Findings):
 * =========================================================
 * 1. Server MUST "mute" unauthenticated sockets - no data events until auth complete
 * 2. Server MUST validate nonce uniqueness to prevent replay attacks
 * 3. Server MUST NOT rely on client-side auth checks - enforce server-side
 * 4. Server MUST reject non-TLS connections (or use TLS-terminating load balancer)
 * 5. Client-side fingerprint is UX aid, NOT security control - server binds to socket ID
 *
 * REFRESH TOKEN SECURITY (Peer Review Fix):
 * =========================================
 * Refresh tokens are NEVER sent over WebSocket. They remain in HttpOnly cookies
 * and are exchanged via standard HTTP POST /api/auth/refresh. The WebSocket only
 * handles short-lived access tokens.
 */

import { io, Socket } from 'socket.io-client';
import { useGraphStore } from '../stores/graphStore';
import { useThreatStore } from '../stores/threatStore';
import { useUIStore } from '../stores/uiStore';
import { queryClient, queryKeys } from './queryClient';
import type { Threat, LDCStatus } from '../types';
import type { TopologyNode } from '@stores/graphStore';
import { useAuthStore } from '../stores/authStore';
import {
  createValidatedHandler,
  ldcStatusSchema,
  deviceSchema,
  deviceUpdateSchema,
  deviceRemoveSchema,
  threatSchema,
  threatUpdateSchema,
  threatResolvedSchema,
  policyUpdateSchema,
  remediationProgressSchema,
  inoculationProgressSchema,
  syncCompleteSchema,
  systemAlertSchema,
  authSuccessSchema,
  authFailureSchema,
  tokenRefreshRequiredSchema,
  type LDCStatusMessage,
  type DeviceMessage,
  type DeviceUpdateMessage,
  type DeviceRemoveMessage,
  type ThreatMessage,
  type ThreatUpdateMessage,
  type ThreatResolvedMessage,
  type PolicyUpdateMessage,
  type RemediationProgressMessage,
  type InoculationProgressMessage,
  type SyncCompleteMessage,
  type SystemAlertMessage,
} from './socketValidation';

// ============================================================================
// Types
// ============================================================================

/**
 * SEC-007: Authentication state for WebSocket connections
 */
interface WebSocketAuthState {
  isAuthenticated: boolean;
  authAttempts: number;
  lastAuthAttempt: Date | null;
  sessionId: string | null;
  userId: string | null;
  deviceFingerprint: string | null;
}

/**
 * SEC-007: Authentication message sent on connect
 *
 * Peer Review Fix: Added nonce for replay attack prevention
 */
interface AuthMessage {
  token: string;
  deviceFingerprint: string;
  sessionId: string;
  timestamp: number;
  /** Peer Review Fix: Random nonce to prevent replay attacks */
  nonce: string;
}

/**
 * SEC-007: Authentication response from server
 */
interface AuthResponse {
  success: boolean;
  sessionId?: string;
  error?: string;
  expiresAt?: number;
}

interface ServerToClientEvents {
  // SEC-007: Authentication events
  'auth:success': (data: AuthResponse) => void;
  'auth:failure': (data: AuthResponse) => void;
  'auth:token_refresh_required': (data: { expiresIn: number }) => void;
  'auth:session_expired': () => void;

  // Existing events
  'ldc:status': (data: LDCStatus) => void;
  'device:update': (data: Partial<TopologyNode> & { id: string }) => void;
  'device:add': (data: TopologyNode) => void;
  'device:remove': (data: { id: string }) => void;
  'threat:new': (data: Threat) => void;
  'threat:update': (data: Partial<Threat> & { id: string }) => void;
  'threat:resolved': (data: { id: string }) => void;
  'policy:update': (data: { id: string }) => void;
  'remediation:progress': (data: { planId: string; actionId: string; status: string; progress: number }) => void;
  'inoculation:progress': (data: { id: string; progress: number; status: string }) => void;
  'sync:complete': (data: { type: string; count: number }) => void;
  'system:alert': (data: { type: string; message: string; severity: string }) => void;
  'connect': () => void;
  'disconnect': (reason: string) => void;
  'connect_error': (error: Error) => void;
}

interface ClientToServerEvents {
  // SEC-007: Authentication events
  // Peer Review Fix: Only auth:authenticate - NO refresh token over WebSocket
  'auth:authenticate': (data: AuthMessage) => void;
  // REMOVED: 'auth:refresh_token' - Refresh tokens stay in HttpOnly cookies

  // Existing events
  'subscribe:ldc': (ldcId: string) => void;
  'unsubscribe:ldc': (ldcId: string) => void;
  'subscribe:threat': (threatId: string) => void;
  'unsubscribe:threat': (threatId: string) => void;
  'ping': () => void;
}

// ============================================================================
// Demo Mode Check
// ============================================================================

import { shouldConnectToApi, isDemoModeEnabled } from '../utils/environment';

/**
 * Check if socket should be initialized
 * Returns false if demo mode is enabled or no API URL is configured
 */
function shouldInitializeSocket(): boolean {
  return shouldConnectToApi();
}

// ============================================================================
// Socket Instance & Authentication State (SEC-007)
// ============================================================================

// Export socket instance for direct access in components
export let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 30000;

// SEC-007: Authentication constants
const AUTH_TIMEOUT_MS = 10000; // 10 seconds to authenticate
const MAX_AUTH_ATTEMPTS = 3;
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh 5 min before expiry

// SEC-007: Authentication state (NOT persisted - security)
let authState: WebSocketAuthState = {
  isAuthenticated: false,
  authAttempts: 0,
  lastAuthAttempt: null,
  sessionId: null,
  userId: null,
  deviceFingerprint: null,
};

// SEC-007: Auth timeout handle
let authTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
let tokenRefreshHandle: ReturnType<typeof setTimeout> | null = null;

// ============================================================================
// SEC-007: Device Fingerprint Generation
// ============================================================================

/**
 * Generate a device fingerprint for session binding
 * This helps prevent session hijacking by binding the WebSocket to a specific browser/device
 *
 * Note: This is a client-side fingerprint for additional security, not tracking
 */
function generateDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width.toString(),
    screen.height.toString(),
    screen.colorDepth.toString(),
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency.toString() ?? 'unknown',
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory?.toString() ?? 'unknown',
  ];

  // Simple hash - not cryptographic, just for binding
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(16).padStart(16, '0');
}

// ============================================================================
// SEC-007: Authentication Functions
// ============================================================================

/**
 * Authenticate the WebSocket connection
 * Called immediately after connection established
 */
function authenticateConnection(): void {
  const { token, user } = useAuthStore.getState();

  if (!token?.accessToken || !user) {
    console.error('[Socket] SEC-007: Cannot authenticate - no auth token');
    handleAuthFailure({ success: false, error: 'No authentication token' });
    return;
  }

  // Generate or reuse device fingerprint
  if (!authState.deviceFingerprint) {
    authState.deviceFingerprint = generateDeviceFingerprint();
  }

  // Set auth timeout
  authTimeoutHandle = setTimeout(() => {
    if (!authState.isAuthenticated) {
      console.error('[Socket] SEC-007: Authentication timeout');
      handleAuthFailure({ success: false, error: 'Authentication timeout' });
    }
  }, AUTH_TIMEOUT_MS);

  // Increment auth attempts
  authState.authAttempts++;
  authState.lastAuthAttempt = new Date();
  authState.userId = user.id;

  // Peer Review Fix: Generate cryptographic nonce for replay attack prevention
  const nonce = generateNonce();

  // Send authentication message (NOT in URL - SEC-007 requirement)
  const authMessage: AuthMessage = {
    token: token.accessToken,
    deviceFingerprint: authState.deviceFingerprint,
    sessionId: token.accessToken.slice(-16), // Use last 16 chars as session binding
    timestamp: Date.now(),
    nonce, // Peer Review Fix: Server MUST validate nonce uniqueness
  };

  socket?.emit('auth:authenticate', authMessage);

  console.log('[Socket] SEC-007: Authentication message sent (attempt %d, nonce: %s)', authState.authAttempts, nonce.slice(0, 8) + '...');
}

/**
 * Peer Review Fix: Generate cryptographic nonce for replay prevention
 * Server MUST track and invalidate nonces after use
 */
function generateNonce(): string {
  // Use crypto.getRandomValues for cryptographically secure randomness
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Handle successful authentication
 */
function handleAuthSuccess(data: AuthResponse): void {
  // Clear timeout
  if (authTimeoutHandle) {
    clearTimeout(authTimeoutHandle);
    authTimeoutHandle = null;
  }

  authState.isAuthenticated = true;
  authState.sessionId = data.sessionId ?? null;

  console.log('[Socket] SEC-007: Authenticated successfully, session:', data.sessionId);

  // Schedule token refresh if expiry provided
  if (data.expiresAt) {
    scheduleTokenRefresh(data.expiresAt);
  }

  useUIStore.getState().addNotification({
    type: 'success',
    title: 'Secure Connection',
    message: 'Authenticated WebSocket connection established',
    duration: 3000,
  });
}

/**
 * Handle authentication failure
 */
function handleAuthFailure(data: AuthResponse): void {
  // Clear timeout
  if (authTimeoutHandle) {
    clearTimeout(authTimeoutHandle);
    authTimeoutHandle = null;
  }

  authState.isAuthenticated = false;

  console.error('[Socket] SEC-007: Authentication failed:', data.error);

  // Retry if under max attempts
  // Peer Review Fix: Add jitter to prevent "Thundering Herd" on server restart
  if (authState.authAttempts < MAX_AUTH_ATTEMPTS) {
    const baseDelay = 1000 * authState.authAttempts;
    const jitter = Math.random() * 500; // 0-500ms random jitter
    console.log('[Socket] SEC-007: Retrying authentication in %dms...', baseDelay + jitter);
    setTimeout(authenticateConnection, baseDelay + jitter);
    return;
  }

  // Max attempts reached - disconnect
  useUIStore.getState().addNotification({
    type: 'error',
    title: 'Authentication Failed',
    message: 'WebSocket authentication failed. Please log in again.',
    duration: 0, // Stay until dismissed
  });

  // Force disconnect
  socket?.disconnect();

  // Trigger re-login
  useAuthStore.getState().logout();
}

/**
 * Handle token refresh requirement from server
 */
function handleTokenRefreshRequired(data: { expiresIn: number }): void {
  console.log('[Socket] SEC-007: Token refresh required, expires in %d ms', data.expiresIn);

  // Attempt to refresh token
  refreshSocketToken();
}

/**
 * Handle session expiry
 */
function handleSessionExpired(): void {
  console.warn('[Socket] SEC-007: Session expired');

  authState.isAuthenticated = false;
  authState.sessionId = null;

  useUIStore.getState().addNotification({
    type: 'warning',
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    duration: 0,
  });

  socket?.disconnect();
  useAuthStore.getState().logout();
}

/**
 * Schedule token refresh before expiry
 */
function scheduleTokenRefresh(expiresAt: number): void {
  // Clear existing refresh timer
  if (tokenRefreshHandle) {
    clearTimeout(tokenRefreshHandle);
  }

  const now = Date.now();
  const timeUntilRefresh = expiresAt - now - TOKEN_REFRESH_THRESHOLD_MS;

  if (timeUntilRefresh <= 0) {
    // Already past threshold, refresh immediately
    refreshSocketToken();
    return;
  }

  tokenRefreshHandle = setTimeout(() => {
    refreshSocketToken();
  }, timeUntilRefresh);

  console.log('[Socket] SEC-007: Token refresh scheduled in %d ms', timeUntilRefresh);
}

/**
 * Refresh the socket token without disconnecting
 *
 * PEER REVIEW FIX: Refresh tokens are NEVER sent over WebSocket.
 * Instead, we:
 * 1. Call standard HTTP /api/auth/refresh endpoint
 * 2. Get new access token via HTTP (refresh token stays in HttpOnly cookie)
 * 3. Re-authenticate WebSocket with new access token
 *
 * This prevents refresh token exposure over WebSocket protocol.
 */
async function refreshSocketToken(): Promise<void> {
  console.log('[Socket] SEC-007: Refreshing token via HTTP (NOT WebSocket)...');

  const authStore = useAuthStore.getState();

  try {
    // Step 1: Refresh token via HTTP endpoint (refresh token in HttpOnly cookie)
    // authStore.refreshToken() should call POST /api/auth/refresh
    const success = await authStore.refreshToken();

    if (!success) {
      throw new Error('Token refresh via HTTP failed');
    }

    // Step 2: Get the new access token (NOT refresh token)
    const { token } = useAuthStore.getState();

    if (!token || !authState.deviceFingerprint) {
      throw new Error('No access token after refresh');
    }

    // Step 3: Re-authenticate WebSocket with new ACCESS token only
    // NEVER send refresh token over WebSocket
    const nonce = generateNonce();

    socket?.emit('auth:authenticate', {
      token: token.accessToken, // Access token ONLY, short-lived
      deviceFingerprint: authState.deviceFingerprint,
      sessionId: token.accessToken.slice(-16),
      timestamp: Date.now(),
      nonce,
    } satisfies AuthMessage);

    console.log('[Socket] SEC-007: Re-authenticated with new access token');
  } catch (error) {
    console.error('[Socket] SEC-007: Token refresh failed:', error);
    handleSessionExpired();
  }
}

/**
 * Get current authentication state
 */
export function getAuthState(): Readonly<WebSocketAuthState> {
  return { ...authState };
}

/**
 * Check if socket is authenticated
 */
export function isAuthenticated(): boolean {
  return authState.isAuthenticated;
}

// ============================================================================
// Event Handlers (SEC-008: Validated)
// ============================================================================

/**
 * SEC-008: All handlers now receive VALIDATED data via Zod schemas
 * Invalid messages are rejected before reaching these handlers
 */

function handleLdcStatusCore(data: LDCStatusMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring data - not authenticated');
    return;
  }
  useGraphStore.getState().updateLdc(data.id, data as unknown as LDCStatus);
}

function handleDeviceUpdateCore(data: DeviceUpdateMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring device:update - not authenticated');
    return;
  }
  useGraphStore.getState().updateNode(data.id, data as unknown as Partial<TopologyNode>);
}

function handleDeviceAddCore(data: DeviceMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring device:add - not authenticated');
    return;
  }
  useGraphStore.getState().addNode(data as unknown as TopologyNode);
}

function handleDeviceRemoveCore(data: DeviceRemoveMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring device:remove - not authenticated');
    return;
  }
  useGraphStore.getState().removeNode(data.id);
}

function handleThreatNewCore(data: ThreatMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring threat:new - not authenticated');
    return;
  }

  // Queue for frame-locked processing
  useThreatStore.getState().queueUpdate({
    type: 'add',
    threat: data as unknown as Threat,
  });

  // Show notification for high/critical threats
  if (data.severity === 'critical' || data.severity === 'high') {
    useUIStore.getState().addNotification({
      type: 'warning',
      title: `New ${data.severity} Threat`,
      message: `${data.type} detected on ${data.source.deviceName}`,
      duration: 10000,
    });
  }
}

function handleThreatUpdateCore(data: ThreatUpdateMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring threat:update - not authenticated');
    return;
  }
  useThreatStore.getState().queueUpdate({
    type: 'update',
    threat: data as unknown as Partial<Threat> & { id: string },
  });
}

function handleThreatResolvedCore(data: ThreatResolvedMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring threat:resolved - not authenticated');
    return;
  }
  useThreatStore.getState().queueUpdate({
    type: 'update',
    threat: { id: data.id, status: 'remediated' as const, resolvedAt: new Date() },
  });
}

function handlePolicyUpdateCore(_data: PolicyUpdateMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring policy:update - not authenticated');
    return;
  }
  // Invalidate policy cache
  void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
}

function handleRemediationProgressCore(data: RemediationProgressMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring remediation:progress - not authenticated');
    return;
  }
  useThreatStore.getState().updateRemediationAction(
    data.planId,
    data.actionId,
    { status: data.status }
  );

  if (data.status === 'completed' && data.progress === 100) {
    useThreatStore.getState().completeRemediation(data.planId);
  }
}

function handleInoculationProgressCore(data: InoculationProgressMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring inoculation:progress - not authenticated');
    return;
  }
  useThreatStore.getState().updateInoculation(data.id, {
    progress: data.progress,
    status: data.status,
  });
}

function handleSyncCompleteCore(data: SyncCompleteMessage): void {
  // SEC-007: Only process if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Ignoring sync:complete - not authenticated');
    return;
  }
  useUIStore.getState().addNotification({
    type: 'success',
    title: 'Sync Complete',
    message: `${data.type}: ${data.count} items synced`,
    duration: 3000,
  });
}

function handleSystemAlertCore(data: SystemAlertMessage): void {
  // SEC-007: System alerts can be processed even if not authenticated
  // (e.g., "authentication required" alerts)
  const notificationType = data.severity === 'critical' || data.severity === 'high'
    ? 'error'
    : data.severity === 'medium'
    ? 'warning'
    : 'info';

  useUIStore.getState().addNotification({
    type: notificationType,
    title: data.type,
    message: data.message,
    duration: data.severity === 'critical' ? 0 : 10000, // Critical stays until dismissed
  });
}

// ============================================================================
// SEC-008: Validated Handlers (wrap core handlers with validation)
// ============================================================================

const handleLdcStatus = createValidatedHandler(ldcStatusSchema, 'ldc:status', handleLdcStatusCore);
const handleDeviceUpdate = createValidatedHandler(deviceUpdateSchema, 'device:update', handleDeviceUpdateCore);
const handleDeviceAdd = createValidatedHandler(deviceSchema, 'device:add', handleDeviceAddCore);
const handleDeviceRemove = createValidatedHandler(deviceRemoveSchema, 'device:remove', handleDeviceRemoveCore);
const handleThreatNew = createValidatedHandler(threatSchema, 'threat:new', handleThreatNewCore);
const handleThreatUpdate = createValidatedHandler(threatUpdateSchema, 'threat:update', handleThreatUpdateCore);
const handleThreatResolved = createValidatedHandler(threatResolvedSchema, 'threat:resolved', handleThreatResolvedCore);
const handlePolicyUpdate = createValidatedHandler(policyUpdateSchema, 'policy:update', handlePolicyUpdateCore);
const handleRemediationProgress = createValidatedHandler(remediationProgressSchema, 'remediation:progress', handleRemediationProgressCore);
const handleInoculationProgress = createValidatedHandler(inoculationProgressSchema, 'inoculation:progress', handleInoculationProgressCore);
const handleSyncComplete = createValidatedHandler(syncCompleteSchema, 'sync:complete', handleSyncCompleteCore);
const handleSystemAlert = createValidatedHandler(systemAlertSchema, 'system:alert', handleSystemAlertCore);

function handleConnect(): void {
  reconnectAttempts = 0;

  // SEC-007: Connection established, but NOT authenticated yet
  console.log('[Socket] Connected to MDC - initiating authentication');

  // DO NOT show "connected" notification until authenticated
  // This prevents the user thinking they're secure when they're not

  // SEC-007: Immediately authenticate on connect
  authenticateConnection();
}

function handleDisconnect(reason: string): void {
  useUIStore.getState().addNotification({
    type: 'warning',
    title: 'Disconnected',
    message: `Connection lost: ${reason}`,
    duration: 5000,
  });
  
  console.log('[Socket] Disconnected:', reason);
}

function handleConnectError(error: Error): void {
  reconnectAttempts++;
  
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    useUIStore.getState().addNotification({
      type: 'error',
      title: 'Connection Failed',
      message: 'Unable to establish real-time connection. Some features may be unavailable.',
      duration: 0,
    });
  }
  
  console.error('[Socket] Connection error:', error.message);
}

// ============================================================================
// Socket Management
// ============================================================================

/**
 * SEC-007: Enforce secure WebSocket URL
 */
function enforceSecureUrl(url: string): string {
  // In production, ALWAYS use wss://
  // In development, allow ws:// for localhost only
  const isProduction = process.env.NODE_ENV === 'production';
  const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1');

  if (isProduction && url.startsWith('ws://')) {
    console.warn('[Socket] SEC-007: Upgrading insecure ws:// to wss://');
    return url.replace('ws://', 'wss://');
  }

  if (isProduction && url.startsWith('http://')) {
    console.warn('[Socket] SEC-007: Upgrading insecure http:// to https://');
    return url.replace('http://', 'https://');
  }

  // Allow insecure in development for localhost only
  if (!isProduction && isLocalhost) {
    return url;
  }

  // For non-localhost in development, still enforce https
  if (!isProduction && !isLocalhost && url.startsWith('http://')) {
    console.warn('[Socket] SEC-007: Upgrading non-localhost http:// to https://');
    return url.replace('http://', 'https://');
  }

  return url;
}

export function initSocket(url?: string): Socket<ServerToClientEvents, ClientToServerEvents> | null {
  // CRITICAL: Do NOT initialize socket in demo mode
  if (!shouldInitializeSocket()) {
    console.log('[Socket] Demo mode enabled or no API URL - socket NOT initialized');
    return null;
  }

  if (socket) {
    return socket;
  }

  // SEC-007: Reset auth state on new connection
  authState = {
    isAuthenticated: false,
    authAttempts: 0,
    lastAuthAttempt: null,
    sessionId: null,
    userId: null,
    deviceFingerprint: generateDeviceFingerprint(),
  };

  // SEC-007: Enforce secure URL
  const rawUrl = url ?? import.meta.env.VITE_WS_URL ?? '';
  const socketUrl = enforceSecureUrl(rawUrl);

  console.log('[Socket] SEC-007: Connecting to', socketUrl);

  socket = io(socketUrl, {
    // SEC-007: Prefer WebSocket, fall back to polling if needed
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
    reconnectionDelay: RECONNECT_DELAY_BASE,
    reconnectionDelayMax: RECONNECT_DELAY_MAX,
    randomizationFactor: 0.5,
    timeout: 20000,

    // SEC-007: DO NOT pass token in URL/query - pass in first message instead
    // The auth callback is only for socket.io handshake metadata, not the actual token
    auth: () => {
      // Return only non-sensitive connection metadata
      return {
        clientVersion: '1.0.0',
        fingerprint: authState.deviceFingerprint,
        // DO NOT include token here - it would appear in server logs
      };
    },
  });

  // SEC-007: Register authentication event handlers FIRST
  socket.on('auth:success', handleAuthSuccess);
  socket.on('auth:failure', handleAuthFailure);
  socket.on('auth:token_refresh_required', handleTokenRefreshRequired);
  socket.on('auth:session_expired', handleSessionExpired);

  // Register connection event handlers
  socket.on('connect', handleConnect);
  socket.on('disconnect', handleDisconnect);
  socket.on('connect_error', handleConnectError);

  // Register data event handlers
  socket.on('ldc:status', handleLdcStatus);
  socket.on('device:update', handleDeviceUpdate);
  socket.on('device:add', handleDeviceAdd);
  socket.on('device:remove', handleDeviceRemove);
  socket.on('threat:new', handleThreatNew);
  socket.on('threat:update', handleThreatUpdate);
  socket.on('threat:resolved', handleThreatResolved);
  socket.on('policy:update', handlePolicyUpdate);
  socket.on('remediation:progress', handleRemediationProgress);
  socket.on('inoculation:progress', handleInoculationProgress);
  socket.on('sync:complete', handleSyncComplete);
  socket.on('system:alert', handleSystemAlert);

  return socket;
}

export function getSocket(): Socket<ServerToClientEvents, ClientToServerEvents> | null {
  return socket;
}

export function disconnectSocket(): void {
  // SEC-007: Clear all auth state and timers
  if (authTimeoutHandle) {
    clearTimeout(authTimeoutHandle);
    authTimeoutHandle = null;
  }
  if (tokenRefreshHandle) {
    clearTimeout(tokenRefreshHandle);
    tokenRefreshHandle = null;
  }

  // Reset auth state
  authState = {
    isAuthenticated: false,
    authAttempts: 0,
    lastAuthAttempt: null,
    sessionId: null,
    userId: null,
    deviceFingerprint: null,
  };

  if (socket) {
    socket.disconnect();
    socket = null;
  }

  console.log('[Socket] SEC-007: Disconnected and auth state cleared');
}

// ============================================================================
// Subscription Helpers (SEC-007: Auth-gated)
// ============================================================================

export function subscribeToLdc(ldcId: string): void {
  // SEC-007: Only allow subscriptions if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Cannot subscribe to LDC - not authenticated');
    return;
  }
  socket?.emit('subscribe:ldc', ldcId);
}

export function unsubscribeFromLdc(ldcId: string): void {
  // SEC-007: Allow unsubscribe even if not authenticated (cleanup)
  socket?.emit('unsubscribe:ldc', ldcId);
}

export function subscribeToThreat(threatId: string): void {
  // SEC-007: Only allow subscriptions if authenticated
  if (!authState.isAuthenticated) {
    console.warn('[Socket] SEC-007: Cannot subscribe to threat - not authenticated');
    return;
  }
  socket?.emit('subscribe:threat', threatId);
}

export function unsubscribeFromThreat(threatId: string): void {
  // SEC-007: Allow unsubscribe even if not authenticated (cleanup)
  socket?.emit('unsubscribe:threat', threatId);
}

// ============================================================================
// Connection Status
// ============================================================================

export function isConnected(): boolean {
  return socket?.connected ?? false;
}

export function getConnectionState(): 'connected' | 'connecting' | 'disconnected' {
  if (!socket) {return 'disconnected';}
  if (socket.connected) {return 'connected';}
  return 'connecting';
}

// ============================================================================
// Frame-Locked Update Processing
// ============================================================================

let processingInterval: number | null = null;

/**
 * Start processing pending socket updates in sync with requestAnimationFrame.
 * This prevents jitter from rapid socket events.
 * 
 * NOTE: In demo mode, this is a no-op since there are no socket events.
 */
export function startFrameLockedProcessing(): void {
  // In demo mode, don't start processing (no socket events to process)
  if (!shouldInitializeSocket()) {
    console.log('[Socket] Demo mode - frame-locked processing not started');
    return;
  }

  if (processingInterval !== null) {return;}
  
  const processUpdates = (): void => {
    // Process up to 10 threat updates per frame to prevent stuttering
    useThreatStore.getState().processPendingUpdates(10);
    
    processingInterval = requestAnimationFrame(processUpdates);
  };
  
  processingInterval = requestAnimationFrame(processUpdates);
}

/**
 * Stop frame-locked processing.
 */
export function stopFrameLockedProcessing(): void {
  if (processingInterval !== null) {
    cancelAnimationFrame(processingInterval);
    processingInterval = null;
  }
}

// ============================================================================
// React Hook for Socket Status (SEC-007: Includes Auth State)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';

export interface SocketStatusState {
  connected: boolean;
  authenticated: boolean;
  reconnecting: boolean;
  error: string | null;
  sessionId: string | null;
}

export function useSocketStatus(): SocketStatusState {
  const [status, setStatus] = useState<SocketStatusState>({
    connected: socket?.connected ?? false,
    authenticated: authState.isAuthenticated,
    reconnecting: false,
    error: null,
    sessionId: authState.sessionId,
  });

  useEffect(() => {
    if (!socket) {return;}

    const handleConnect = (): void => {
      // SEC-007: Connected but not yet authenticated
      setStatus(prev => ({
        ...prev,
        connected: true,
        reconnecting: false,
        error: null,
        authenticated: false, // Will update on auth:success
      }));
    };

    const handleDisconnect = (): void => {
      setStatus(prev => ({
        ...prev,
        connected: false,
        authenticated: false,
        sessionId: null,
      }));
    };

    const handleReconnecting = (): void => {
      setStatus(prev => ({ ...prev, reconnecting: true }));
    };

    const handleError = (err: Error): void => {
      setStatus(prev => ({ ...prev, error: err.message }));
    };

    // SEC-007: Auth success/failure handlers
    const handleAuthSuccessStatus = (data: AuthResponse): void => {
      setStatus(prev => ({
        ...prev,
        authenticated: true,
        sessionId: data.sessionId ?? null,
        error: null,
      }));
    };

    const handleAuthFailureStatus = (data: AuthResponse): void => {
      setStatus(prev => ({
        ...prev,
        authenticated: false,
        sessionId: null,
        error: data.error ?? 'Authentication failed',
      }));
    };

    const handleSessionExpiredStatus = (): void => {
      setStatus(prev => ({
        ...prev,
        authenticated: false,
        sessionId: null,
        error: 'Session expired',
      }));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.io.on('reconnect_attempt', handleReconnecting);
    socket.on('connect_error', handleError);
    socket.on('auth:success', handleAuthSuccessStatus);
    socket.on('auth:failure', handleAuthFailureStatus);
    socket.on('auth:session_expired', handleSessionExpiredStatus);

    return () => {
      socket?.off('connect', handleConnect);
      socket?.off('disconnect', handleDisconnect);
      socket?.io.off('reconnect_attempt', handleReconnecting);
      socket?.off('connect_error', handleError);
      socket?.off('auth:success', handleAuthSuccessStatus);
      socket?.off('auth:failure', handleAuthFailureStatus);
      socket?.off('auth:session_expired', handleSessionExpiredStatus);
    };
  }, []);

  return status;
}

/**
 * SEC-007: Hook to manually trigger re-authentication
 */
export function useSocketReauthenticate(): () => void {
  return useCallback(() => {
    if (!socket?.connected) {
      console.warn('[Socket] Cannot re-authenticate - not connected');
      return;
    }

    // Reset auth state
    authState.isAuthenticated = false;
    authState.authAttempts = 0;

    // Trigger re-authentication
    authenticateConnection();
  }, []);
}

